import Ajv, { ErrorObject, ValidateFunction } from 'ajv';
import addFormats from 'ajv-formats';

/**
 * BQ-22: Runtime JSON-Schema validation for uploaded NER comparison files.
 *
 * The schema lives at `/data-schema.json` (served from `public/`). We fetch
 * it once at first use, compile an Ajv validator, and cache the compiled
 * function for the lifetime of the page. Failing to load the schema is
 * non-fatal — `validateAgainstSchema` returns `valid: true` so the rest of
 * the pipeline (parseNERContent → parseNERJson) still has a chance to handle
 * the upload. The legacy text path skips this module entirely.
 */

export interface SchemaValidationResult {
  valid: boolean;
  errors: Array<{ path: string; message: string }>;
}

const SCHEMA_URL = '/data-schema.json';

let validatorPromise: Promise<ValidateFunction | null> | null = null;

async function getValidator(): Promise<ValidateFunction | null> {
  if (validatorPromise) return validatorPromise;
  validatorPromise = (async () => {
    try {
      const res = await fetch(SCHEMA_URL);
      if (!res.ok) {
        // Schema not reachable — skip validation rather than fail upload.
        // eslint-disable-next-line no-console
        console.warn('[schemaValidator] could not fetch /data-schema.json:', res.status);
        return null;
      }
      const schema = await res.json();
      // Ajv v8 with strict mode complains about the top-level `examples`
      // keyword (which Draft-07 treats as informational, not validating).
      // strict:false keeps it permissive.
      const ajv = new Ajv({ allErrors: true, strict: false });
      addFormats(ajv);
      return ajv.compile(schema);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[schemaValidator] schema load failed:', e);
      return null;
    }
  })();
  return validatorPromise;
}

function formatError(err: ErrorObject): { path: string; message: string } {
  // instancePath is JSON-pointer ('/examples/2/modelResponses/0/entities/5/start');
  // convert to a more human-friendly dotted path.
  const pointer = err.instancePath || '';
  const path = pointer
    .replace(/^\//, '')
    .split('/')
    .filter((s) => s.length > 0)
    .map((seg) => (/^\d+$/.test(seg) ? `[${seg}]` : seg))
    .join('.')
    .replace(/\.\[/g, '[');

  // Ajv messages like "must be number" read fine on their own; prepend the
  // missing-property name when relevant.
  let message = err.message ?? 'is invalid';
  if (err.keyword === 'required' && err.params && typeof (err.params as any).missingProperty === 'string') {
    message = `is missing required property '${(err.params as any).missingProperty}'`;
  }

  return { path: path || '(root)', message };
}

/**
 * Validate a parsed JSON object against the bundled data schema.
 *
 * - On first call, fetches /data-schema.json, compiles with Ajv, caches.
 * - If the schema can't be loaded, returns { valid: true, errors: [] } so
 *   the legacy pipeline still runs.
 * - On invalid input, returns { valid: false, errors: [...] } with each
 *   error keyed by a dotted JSON path.
 */
export async function validateAgainstSchema(data: unknown): Promise<SchemaValidationResult> {
  const validate = await getValidator();
  if (!validate) {
    return { valid: true, errors: [] };
  }
  const valid = validate(data);
  if (valid) {
    return { valid: true, errors: [] };
  }
  const errors = (validate.errors ?? []).map(formatError);
  return { valid: false, errors };
}

/**
 * Test-only helper: drop the cached validator so unit tests can swap the
 * schema fetch between cases. Not exported from the package's public API.
 */
export function __resetSchemaValidatorCacheForTests(): void {
  validatorPromise = null;
}
